import { Component, EventEmitter, inject, InjectFlags, Input, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { Option, UserSkillModel } from '@digilize/shared/definitions/src';
import { SkillType } from '@digilize/shared/definitions/src/lib/enums';
import { forkJoin } from 'rxjs';

import { createOptions } from '../../../helpers/helpers';
import { ProfessionsService } from '../../../services/professions.service';
import { SkillsService } from '../../../services/skills.service';
import { WizardService } from '../../../services/wizard.service';
import { CV_PARSED_SKILL_IDS_KEY } from '../cv-skills/cv-skills.component';
import { DesiredOccupationsComponent } from '../desired-occupations/desired-occupations.component';

interface ProfessionSkillGroup {
  professionId: string;
  professionName: string;
  skills: Option[];
  loaded: boolean;
  selectedSkillIds: Set<string>;
}

@Component({
  selector: 'bwc-profession-skills',
  templateUrl: './profession-skills.component.html',
  styleUrls: ['./profession-skills.component.scss'],
})
export class ProfessionSkillsComponent implements OnInit {
  @Input() prevBut: boolean;
  @Input() closeBut: boolean;
  @Input() maxWorkProfessions = 2;
  @Input() maxDreamProfessions = 2;
  @Input() mode: 'work' | 'dream' | 'all' = 'all';
  @Output() emitClose = new EventEmitter<string>();

  wizardService = inject(WizardService, InjectFlags.Optional);

  form: FormGroup;
  professionGroups: ProfessionSkillGroup[] = [];
  currentProfessionIndex = 0;
  dataLoaded = false;

  private readonly fb = inject(FormBuilder);
  private readonly skillsService = inject(SkillsService);
  private readonly professionsService = inject(ProfessionsService);

  ngOnInit(): void {
    this.form = this.fb.group({
      addedSkills: this.fb.array([]),
    });
    this.loadData();
  }

  get addedSkills(): FormArray {
    return this.form.get('addedSkills') as FormArray;
  }

  get currentGroup(): ProfessionSkillGroup | null {
    return this.professionGroups[this.currentProfessionIndex] ?? null;
  }

  toggleSkill(skill: Option): void {
    const group = this.currentGroup;
    if (!group || !skill.id) return;
    const existing = this.addedSkills.controls.find((c) => c.value.skill_id === skill.id);
    if (existing) {
      const isActive = !existing.value.active;
      if (isActive) {
        const req = this.buildUserSkillReq(existing.value.skill_id, existing.value.skill_name);
        this.skillsService.addUserSkill(req).subscribe((savedSkill) => {
          existing.setValue({ ...existing.value, id: savedSkill.id, active: true });
          group.selectedSkillIds.add(skill.id as string);
        });
      } else {
        this.skillsService.deleteUserSkill(existing.value.id).subscribe(() => {
          existing.setValue({ ...existing.value, active: false });
          group.selectedSkillIds.delete(skill.id as string);
        });
      }
    } else {
      const req = this.buildUserSkillReq(skill.id, skill.text);
      this.skillsService.addUserSkill(req).subscribe((savedSkill) => {
        this.addedSkills.push(
          this.fb.control({
            id: savedSkill.id,
            skill_id: savedSkill.skill_id ?? skill.id,
            skill_name: savedSkill.skill_name ?? skill.text,
            rating: savedSkill.rating ?? 0,
            active: true,
          })
        );
        group.selectedSkillIds.add(skill.id as string);
      });
    }
  }

  isSkillAdded(skill: Option): boolean {
    return !!skill.id && (this.currentGroup?.selectedSkillIds.has(skill.id) ?? false);
  }

  goPrev(): void {
    if (this.currentProfessionIndex > 0) {
      this.currentProfessionIndex--;
      this.loadSkillsForGroup(this.currentGroup);
    } else if (this.wizardService?.isManualFlow) {
      this.wizardService.goToStepByComponent(DesiredOccupationsComponent);
    } else {
      this.wizardService?.goToPrevious();
    }
  }

  onSubmit(): void {
    if (this.currentProfessionIndex < this.professionGroups.length - 1) {
      this.currentProfessionIndex++;
      this.loadSkillsForGroup(this.currentGroup);
    } else {
      this.wizardService?.goToNext();
    }
  }

  close(): void {
    this.emitClose.emit('profession_skills');
  }

  private autoSkip(): void {
    if (this.wizardService?.lastDirection === 'back') {
      if (this.wizardService.isManualFlow) {
        this.wizardService.goToStepByComponent(DesiredOccupationsComponent);
      } else {
        this.wizardService.goToPrevious();
      }
      return;
    }
    this.wizardService?.goToNext();
  }

  private loadData(): void {
    forkJoin({
      workProfessions: this.professionsService.getProfessionsMy(),
      dreamProfessions: this.professionsService.getDreamProfessionsMy(),
      existingSkills: this.skillsService.getAllUserSkills(),
    }).subscribe(({ workProfessions, dreamProfessions, existingSkills }) => {
      if (existingSkills?.data?.length > 0) {
        existingSkills.data.forEach((item) => {
          this.addedSkills.push(
            this.fb.control({
              id: item.id,
              skill_id: item.skill_id,
              skill_name: item.skill_name,
              rating: item.rating,
              active: true,
            })
          );
        });
      }

      const seen = new Set<string>();
      const groups: ProfessionSkillGroup[] = [];

      if (this.mode === 'work' || this.mode === 'all') {
        let workCount = 0;
        for (const wp of [...(workProfessions.data ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
          if (wp.position_id && !seen.has(wp.position_id) && workCount < this.maxWorkProfessions) {
            seen.add(wp.position_id);
            workCount++;
            groups.push({
              professionId: wp.position_id,
              professionName: wp.position_name ?? '',
              skills: [],
              loaded: false,
              selectedSkillIds: new Set<string>(),
            });
          }
        }
      }

      if (this.mode === 'dream' || this.mode === 'all') {
        let dreamCount = 0;
        for (const dp of [...(dreamProfessions.data ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))) {
          if (dp.position_id && !seen.has(dp.position_id) && dreamCount < this.maxDreamProfessions) {
            seen.add(dp.position_id);
            dreamCount++;
            groups.push({
              professionId: dp.position_id,
              professionName: dp.name ?? '',
              skills: [],
              loaded: false,
              selectedSkillIds: new Set<string>(),
            });
          }
        }
      }

      this.professionGroups = groups;
      this.dataLoaded = true;

      if (this.professionGroups.length === 0) {
        this.autoSkip();
        return;
      }

      this.loadSkillsForGroup(this.professionGroups[0]);
    });
  }

  private getCvParsedSkillIds(): string[] {
    try {
      const raw = sessionStorage.getItem(CV_PARSED_SKILL_IDS_KEY);
      const userSkillIds: string[] = raw ? JSON.parse(raw) : [];
      if (!userSkillIds.length) return [];
      const idSet = new Set(userSkillIds);
      return this.addedSkills.controls
        .filter((c) => idSet.has(c.value.id))
        .map((c) => c.value.skill_id as string)
        .filter(Boolean);
    } catch {
      return [];
    }
  }

  private loadSkillsForGroup(group: ProfessionSkillGroup): void {
    const excludeIds: string[] = [...this.getCvParsedSkillIds()];
    for (const g of this.professionGroups) {
      if (g !== group) g.selectedSkillIds.forEach((id) => excludeIds.push(id));
    }
    this.skillsService
      .getSkillsByProfession(group.professionId, [SkillType.Language], excludeIds)
      .subscribe((response) => {
        group.skills = createOptions(response);
        group.loaded = true;
        const claimedElsewhere = new Set<string>();
        for (const g of this.professionGroups) {
          if (g !== group) g.selectedSkillIds.forEach((id) => claimedElsewhere.add(id));
        }
        for (const opt of group.skills) {
          if (!opt.id || claimedElsewhere.has(opt.id) || group.selectedSkillIds.has(opt.id)) continue;
          const active = this.addedSkills.controls.some(
            (c) => c.value.skill_id === opt.id && c.value.active
          );
          if (active) group.selectedSkillIds.add(opt.id);
        }
      });
  }

  private buildUserSkillReq(skillId: string | null, skillName: string): UserSkillModel {
    return {
      id: null as unknown as string,
      skill_id: skillId,
      skill_name: skillName,
      skill_mapping_name: null,
      rating: 0,
      rating_written: null,
      is_native: null,
      type: SkillType.Unknown,
      order: 0,
    };
  }
}
